import { useState, useEffect } from "react";
import { Nav, Footer, GlobalStyles, theme, getCareConfig } from "./design-system";

function getSlug() {
  const p = window.location.pathname.split("/");
  return p[p.length - 1];
}

function InfoSection({ title, items, dotColor, labelColor, accentTop }) {
  if (!items?.length) return null;
  return (
    <div style={{ background: theme.surface, borderRadius: theme.radius, padding: "1.5rem 1.6rem", border: `1px solid ${theme.border}`, borderTop: `3px solid ${accentTop}`, boxShadow: theme.shadow }}>
      <h2 style={{ fontSize: "0.72rem", fontWeight: "700", color: labelColor, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 1rem" }}>{title}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: "flex", gap: "0.7rem", alignItems: "flex-start" }}>
            <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: dotColor + "20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "2px" }}>
              <span style={{ color: dotColor, fontSize: "0.55rem", fontWeight: "800" }}>✓</span>
            </div>
            <p style={{ margin: 0, fontSize: "0.87rem", color: theme.textMid, lineHeight: 1.65 }}>{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertSection({ title, items, bg, borderColor, textColor }) {
  if (!items?.length) return null;
  return (
    <div style={{ background: bg, borderRadius: theme.radius, padding: "1.4rem 1.6rem", border: `1px solid ${borderColor}` }}>
      <h2 style={{ fontSize: "0.72rem", fontWeight: "700", color: textColor, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 0.75rem" }}>{title}</h2>
      {items.map((item, i) => (
        <div key={i} style={{ display: "flex", gap: "0.55rem", marginBottom: "0.35rem" }}>
          <span style={{ color: textColor, flexShrink: 0, fontSize: "0.85rem" }}>•</span>
          <p style={{ margin: 0, fontSize: "0.85rem", color: textColor, lineHeight: 1.6, fontWeight: "500" }}>{item}</p>
        </div>
      ))}
    </div>
  );
}

export default function ConditionDetails() {
  const [guidance, setGuidance]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const slug = getSlug();

  useEffect(() => {
    Promise.all([
      fetch(`https://co-6008-ai-chatbot-5yr6.vercel.app/api/conditions/${slug}`).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
      fetch(`https://co-6008-ai-chatbot-5yr6.vercel.app/api/guidance/${slug}`).then(r => r.json()),
    ]).then(([cond, guid]) => { setGuidance(guid); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug]);

  const care = guidance ? getCareConfig(guidance.careLevel) : null;

  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav />

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <div style={{ width: "34px", height: "34px", border: `2.5px solid ${theme.border}`, borderTop: `2.5px solid ${theme.clay}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      )}

      {error && (
        <div style={{ maxWidth: "600px", margin: "5rem auto", padding: "0 2rem", textAlign: "center" }}>
          <p style={{ fontFamily: theme.fontDisplay, fontSize: "1.3rem", color: theme.text, marginBottom: "0.5rem" }}>Condition not found</p>
          <p style={{ color: theme.textMid, fontSize: "0.88rem", marginBottom: "1.25rem" }}>We couldn't find information for this condition.</p>
          <a href="/conditions" style={{ background: theme.clay, color: "#fff", textDecoration: "none", padding: "0.6rem 1.4rem", borderRadius: theme.radiusPill, fontSize: "0.85rem", fontWeight: "600" }}>Browse all conditions</a>
        </div>
      )}

      {!loading && !error && guidance && (
        <>
          {/* HEADER */}
          <div style={{ padding: "3.5rem 2rem 2.5rem", borderBottom: `1px solid ${theme.border}`, background: theme.bg }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
              {/* Breadcrumb */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", marginBottom: "1rem" }}>
                <a href="/conditions" style={{ color: theme.textLight, fontSize: "0.78rem", textDecoration: "none", transition: "color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.color = theme.clay}
                  onMouseLeave={e => e.currentTarget.style.color = theme.textLight}
                >Conditions</a>
                <span style={{ color: theme.textLight, fontSize: "0.78rem" }}>›</span>
                <span style={{ color: theme.textMid, fontSize: "0.78rem" }}>{guidance.category}</span>
              </div>

              <h1 style={{ fontFamily: theme.fontDisplay, fontSize: "clamp(1.8rem, 3vw, 2.7rem)", fontWeight: "500", color: theme.text, margin: "0 0 1.25rem", lineHeight: 1.2 }}>
                {guidance.name}
              </h1>

              <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.42rem", background: care.bg, color: care.text, border: `1px solid ${care.dot}44`, padding: "0.38rem 0.95rem", borderRadius: theme.radiusPill }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: care.dot }} />
                  <span style={{ fontSize: "0.74rem", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Recommended: {care.label}
                  </span>
                </div>
                <span style={{ color: theme.textLight, fontSize: "0.74rem", background: theme.bgDeep, padding: "0.32rem 0.8rem", borderRadius: theme.radiusPill, border: `1px solid ${theme.border}` }}>
                  {guidance.category}
                </span>
              </div>
            </div>
          </div>

          {/* EMERGENCY STRIP */}
          <div style={{ background: theme.redLight, borderBottom: `1px solid ${theme.red}44`, padding: "0.55rem 2rem" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
              <p style={{ color: theme.red, fontSize: "0.77rem", margin: 0, fontWeight: "600" }}>
                🚨 Life-threatening emergency? Call <strong>999</strong> immediately. Do not use this tool.
              </p>
            </div>
          </div>

          {/* CONTENT */}
          <div style={{ maxWidth: "860px", margin: "0 auto", padding: "2rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>

              {/* Overview */}
              <div style={{ background: theme.surface, borderRadius: theme.radius, padding: "1.6rem", border: `1px solid ${theme.border}`, borderTop: `3px solid ${theme.clay}`, boxShadow: theme.shadow }}>
                <h2 style={{ fontSize: "0.72rem", fontWeight: "700", color: theme.clay, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 0.75rem" }}>Overview</h2>
                <p style={{ color: theme.textMid, lineHeight: 1.75, margin: 0, fontSize: "0.95rem" }}>{guidance.overview}</p>
              </div>

              <InfoSection
                title="Self-Care"
                items={guidance.selfCare}
                dotColor={theme.green}
                labelColor={theme.green}
                accentTop={theme.green}
              />

              <InfoSection
                title="See a GP if..."
                items={guidance.seeGP}
                dotColor={theme.amber}
                labelColor={theme.amber}
                accentTop={theme.amber}
              />

              <AlertSection
                title="⚠ Warning Signs"
                items={guidance.redFlags}
                bg={theme.amberLight}
                borderColor={theme.amber + "55"}
                textColor={theme.clayDark}
              />

              <AlertSection
                title="🚨 Call 999 if..."
                items={guidance.call999}
                bg={theme.redLight}
                borderColor={theme.red + "44"}
                textColor={theme.red}
              />

              {/* CTA */}
              <div style={{ background: theme.bgDeep, borderRadius: theme.radius, padding: "1.4rem 1.6rem", border: `1px solid ${theme.border}`, display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <p style={{ fontWeight: "700", color: theme.text, margin: "0 0 0.25rem", fontSize: "0.92rem" }}>Not sure what to do?</p>
                  <p style={{ color: theme.textMid, fontSize: "0.81rem", margin: 0 }}>Use CareNav for personalised AI-guided symptom assessment.</p>
                </div>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent("openChatWidget"))}
                  style={{ background: theme.clay, color: "#fff", border: "none", padding: "0.65rem 1.4rem", borderRadius: theme.radiusPill, fontFamily: theme.fontBody, fontWeight: "700", fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.clayDark}
                  onMouseLeave={e => e.currentTarget.style.background = theme.clay}
                >Start Symptom Check</button>
              </div>

              <a href="/conditions" style={{ color: theme.clay, fontSize: "0.83rem", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
              >← Back to all conditions</a>

              <p style={{ color: theme.textLight, fontSize: "0.73rem", lineHeight: 1.65, borderTop: `1px solid ${theme.border}`, paddingTop: "1rem", margin: 0 }}>
                This information is for general guidance only and does not constitute medical advice. Always consult a qualified healthcare professional if you are concerned.
              </p>
            </div>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
}